import { APIService } from './core/APIService.js';
import { EventBus } from './core/EventBus.js';
import { FeedbackSDK } from './core/FeedbackSDK.js';
import { CSS_STYLES } from './styles/styles.js';
import {
	APIError,
	ConfigError,
	SDKError,
	ValidationError,
	WidgetError,
} from './utils/errors.js';
import * as helpers from './utils/helpers.js';
import { BaseWidget } from './widgets/BaseWidget.js';
import { ButtonWidget } from './widgets/ButtonWidget.js';
import { ChangelogWidget } from './widgets/ChangelogWidget.js';
import { InlineWidget } from './widgets/InlineWidget.js';
import { MessengerWidget } from './widgets/MessengerWidget.js';
import { SurveyWidget } from './widgets/SurveyWidget.js';
import { TabWidget } from './widgets/TabWidget.js';
import { WidgetFactory } from './widgets/WidgetFactory.js';

function injectStyles() {
	if (typeof document === 'undefined') return;

	if (!document.querySelector('#feedback-sdk-styles')) {
		const style = document.createElement('style');
		style.id = 'feedback-sdk-styles';
		style.textContent = CSS_STYLES;
		document.head.appendChild(style);
	}

	if (!document.querySelector('#feedback-sdk-iconify')) {
		const script = document.createElement('script');
		script.id = 'feedback-sdk-iconify';
		script.src =
			'https://cdn.jsdelivr.net/npm/iconify-icon@2/dist/iconify-icon.min.js';
		script.async = true;
		document.head.appendChild(script);
	}
}

function autoInit() {
	if (typeof window !== 'undefined' && window.FeedbackSDKConfig) {
		injectStyles();

		const config = { ...window.FeedbackSDKConfig };
		const sdk = new FeedbackSDK(config);

		sdk
			.init()
			.then((initData) => {
				window.FeedbackSDK.instance = sdk;

				if (window.FeedbackSDKConfig.autoCreate) {
					const widgets = Array.isArray(window.FeedbackSDKConfig.autoCreate)
						? window.FeedbackSDKConfig.autoCreate
						: [window.FeedbackSDKConfig.autoCreate];

					widgets.forEach((widgetConfig) => {
						try {
							const widget = sdk.createWidget(
								widgetConfig.type || 'button',
								widgetConfig
							);
							widget.mount(widgetConfig.container);
						} catch (error) {
							console.error('[FeedbackSDK] Failed to create widget:', error);
						}
					});
				}

				if (typeof CustomEvent !== 'undefined') {
					const event = new CustomEvent('FeedbackSDKReady', {
						detail: { sdk, config, initData },
					});
					window.dispatchEvent(event);
				}
			})
			.catch((error) => {
				console.error('[FeedbackSDK] Auto-initialization failed:', error);

				if (typeof CustomEvent !== 'undefined') {
					const event = new CustomEvent('FeedbackSDKError', {
						detail: { error, config, phase: 'initialization' },
					});
					window.dispatchEvent(event);
				}
			});
	}
}

function handleDOMReady() {
	if (typeof document !== 'undefined') {
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', autoInit);
		} else {
			setTimeout(autoInit, 0);
		}
	}
}

const FeedbackSDKExport = {
	FeedbackSDK,
	BaseWidget,
	ButtonWidget,
	ChangelogWidget,
	TabWidget,
	InlineWidget,
	SurveyWidget,
	MessengerWidget,
	WidgetFactory,
	EventBus,
	APIService,
	SDKError,
	APIError,
	WidgetError,
	ConfigError,
	ValidationError,
	helpers,
	create: (config) => {
		injectStyles();
		return new FeedbackSDK(config);
	},
	version: '1.0.0',
	instance: null,

	isReady: () => Boolean(FeedbackSDKExport.instance),
	getInstance: () => FeedbackSDKExport.instance,

	setUserContext: (userContext) => {
		if (FeedbackSDKExport.instance) {
			FeedbackSDKExport.instance.setUserContext(userContext);
		} else {
			if (typeof window !== 'undefined') {
				window.FeedbackSDKUserContext = userContext;
			}
		}
	},

	initWithUser: async (config, userContext) => {
		injectStyles();
		const fullConfig = { ...config, userContext };
		const sdk = new FeedbackSDK(fullConfig);
		await sdk.init();

		if (typeof window !== 'undefined') {
			window.FeedbackSDK.instance = sdk;
		}

		return sdk;
	},

	onReady: (callback) => {
		if (typeof window !== 'undefined') {
			if (FeedbackSDKExport.isReady()) {
				callback(FeedbackSDKExport.instance);
			} else {
				window.addEventListener(
					'FeedbackSDKReady',
					(event) => {
						callback(event.detail.sdk, event.detail);
					},
					{ once: true }
				);
			}
		}
	},

	onError: (callback) => {
		if (typeof window !== 'undefined') {
			window.addEventListener('FeedbackSDKError', (event) => {
				callback(event.detail.error, event.detail);
			});
		}
	},

	extractUserContext: FeedbackSDK.extractUserContextFromAuth,
};

if (typeof window !== 'undefined') {
	window.FeedbackSDK = FeedbackSDKExport;
	handleDOMReady();
}

export default FeedbackSDKExport;

export {
	APIError,
	APIService,
	BaseWidget,
	ButtonWidget,
	ChangelogWidget,
	ConfigError,
	EventBus,
	FeedbackSDK,
	helpers,
	InlineWidget,
	MessengerWidget,
	SDKError,
	SurveyWidget,
	TabWidget,
	ValidationError,
	WidgetError,
	WidgetFactory,
};
